"use client"

import { useState } from "react"
import { Form, Formik } from "formik"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { getAiDisclosureDetailsAction, getAiDisclosureHistoryAction } from "@/server-actions/ai-disclosure-settings"
import { disclosureDetailsCsv, disclosureCsvHeader } from "@/lib/ai-disclosure/report-csv"
import type { DisclosureDetail } from "@/lib/ai-disclosure/report"
import { DisclosureReportTables } from "@/components/organization/disclosure-report-tables"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"


export function AiDisclosureReport() {
  const tUi = useTranslations("Organization.AiDisclosureReport")

  const [range, setRange] = useState(() => {
    const today = new Date()
    const start = new Date(today)
    start.setUTCDate(start.getUTCDate() - 30)
    return { from: isoDay(start), to: isoDay(today) }
  })
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const from = `${range.from}T00:00:00.000Z`
  const to = afterDay(range.to)

  const exportCsv = async () => {
    setExporting(true)
    setExportError(null)
    try {
      const lines = [disclosureCsvHeader]
      const details: DisclosureDetail[] = []
      const history = await getAiDisclosureHistoryAction({ from, to })
      if ("status" in history) throw new Error(history.errorMessage)
      let cursor: string | undefined
      do {
        const result = await getAiDisclosureDetailsAction({ from, to, cursor })
        if ("status" in result) throw new Error(result.errorMessage)
        details.push(...result.details)
        cursor = result.nextCursor ?? undefined
      } while (cursor)
      lines.push(...disclosureDetailsCsv(details, history))
      const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" }))
      const link = document.createElement("a")
      link.href = url
      link.download = `ai-disclosure-details-${range.from}-${range.to}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Could not export disclosure details.")
    } finally {
      setExporting(false)
    }
  }

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle><span key={tUi("aIDisclosureDeliveryReport")}>{tUi("aIDisclosureDeliveryReport")}</span></CardTitle>
        <CardDescription>
          <TranslationText>{tUi.rich("reportDescription", {
            requirements: chunks => <a href="https://ai-act-service-desk.ec.europa.eu/en/ai-act/article-50" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-foreground">{chunks}</a>,
          })}</TranslationText>
        </CardDescription>
      </CardHeader>
      <CardContent className="min-w-0 space-y-4">
        <Formik initialValues={range} onSubmit={values => { setRange(values); setExportError(null); return Promise.resolve() }}>
          {formik => (
            <Form className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] [&_input]:min-w-0 [&_input]:max-w-full [&_button]:h-auto [&_button]:min-h-10 [&_button]:whitespace-normal">
              <div className="min-w-0 space-y-1">
                <Label htmlFor="ai-report-from"><span key={tUi("fromUTC")}>{tUi("fromUTC")}</span></Label>
                <Input id="ai-report-from" type="date" name="from" value={formik.values.from}
                  max={formik.values.to} onChange={formik.handleChange} required />
              </div>
              <div className="min-w-0 space-y-1">
                <Label htmlFor="ai-report-to"><span key={tUi("toUTC")}>{tUi("toUTC")}</span></Label>
                <Input id="ai-report-to" type="date" name="to" value={formik.values.to}
                  min={formik.values.from} onChange={formik.handleChange} required />
              </div>
              <Button type="submit" disabled={formik.isSubmitting || exporting}><span key={tUi("apply")}>{tUi("apply")}</span></Button>
              <Button type="button" variant="outline" disabled={exporting} onClick={() => void exportCsv()}>
                <span key={[String(exporting), exporting ? tUi("exporting") : tUi("exportDetailedCSV")].join(":")}>{exporting ? tUi("exporting") : tUi("exportDetailedCSV")}</span>
              </Button>
            </Form>
          )}
        </Formik>
        {exportError && <p role="alert" className="text-sm text-destructive">{exportError}</p>}
        <p className="text-xs text-muted-foreground">
          <span key={tUi("countsDistinctConversationsWithPersistedAIInteractionsOr")}>{tUi("countsDistinctConversationsWithPersistedAIInteractionsOr")}</span></p>
        <DisclosureReportTables key={`${from}-${to}`} from={from} to={to} />

      </CardContent>
    </Card>
  )
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function afterDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`)
  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString()
}
